/**
 * Units. Domain lengths are integer ticks of 0.1 mm (TICKS_PER_MM = 10).
 * Millimetres appear only at the edges (formatting, placement, exports);
 * metres only at the renderer/Blender boundary.
 */

export const TICKS_PER_MM = 10;

/** Millimetres → ticks (rounded to the nearest 0.1 mm). */
export function mm(n: number | string): number {
  return Math.round(Number(n) * TICKS_PER_MM);
}

/** Ticks → millimetres. */
export function toMm(ticks: number): number {
  return ticks / TICKS_PER_MM;
}

/** Ticks → metres (renderer/Blender boundary). */
export function toMetres(ticks: number): number {
  return ticks / (TICKS_PER_MM * 1000);
}

export type ParseResult = { ok: true; ticks: number } | { ok: false; error: string };

/** Format a millimetre number with at most one decimal ("900", "800.1"). */
export function fmtNum(v: number): string {
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/**
 * Parse user input in millimetres. Accepts "900", "900.5", "900 mm", " 277.5mm ".
 * Rejects other units (with the conversion), negative numbers and anything finer than 0.1 mm.
 */
export function parseMm(input: unknown): ParseResult {
  const s = String(input ?? '').trim().replace(/ /g, ' ');
  if (!s) return { ok: false, error: 'Enter a size in millimetres.' };
  const cm = s.match(/^(\d+(?:[.,]\d+)?)\s*cm$/i);
  if (cm) {
    const v = Number(cm[1]!.replace(',', '.')) * 10;
    return { ok: false, error: `Sizes are in millimetres: ${cm[1]} cm is ${fmtNum(v)} mm.` };
  }
  const inch = s.match(/^(\d+(?:[.,]\d+)?)\s*(?:in|inch|inches|")$/i);
  if (inch) {
    const v = Number(inch[1]!.replace(',', '.')) * 25.4;
    return { ok: false, error: `Sizes are in millimetres: ${inch[1]} in is ${fmtNum(v)} mm.` };
  }
  if (/^\d+(?:[.,]\d+)?\s*m$/i.test(s)) return { ok: false, error: 'Sizes are in millimetres, for example 900.' };
  if (/^-/.test(s)) return { ok: false, error: 'Sizes must be greater than zero.' };
  const m = s.match(/^(\d+)(?:[.,](\d+))?\s*(?:mm)?$/i);
  if (!m) return { ok: false, error: 'Enter a number of millimetres, for example 900 or 277.5.' };
  const frac = m[2] ?? '';
  if (frac.length > 1 && /[1-9]/.test(frac.slice(1))) {
    return { ok: false, error: 'Sizes can be set to 0.1 mm at most, for example 277.5.' };
  }
  const ticks = Number(m[1]) * TICKS_PER_MM + (frac ? Number(frac[0]) : 0);
  if (ticks <= 0) return { ok: false, error: 'Sizes must be greater than zero.' };
  return { ok: true, ticks };
}

/** Ticks → "277.5" (or "277.5 mm" with {unit:true}). Whole millimetres print without decimals. */
export function fmtMm(ticks: number, { unit = false }: { unit?: boolean } = {}): string {
  const neg = ticks < 0;
  const a = Math.abs(Math.round(ticks));
  const whole = Math.floor(a / TICKS_PER_MM);
  const tenth = a % TICKS_PER_MM;
  const s = (neg ? '−' : '') + whole + (tenth ? '.' + tenth : '');
  return unit ? s + ' mm' : s;
}

/** Ticks → "18.0": always one decimal (for measured stock thickness). */
export function fmtMm1(ticks: number): string {
  return (ticks / TICKS_PER_MM).toFixed(1);
}

export function sum(a: readonly number[]): number {
  let s = 0;
  for (const v of a) s += v;
  return s;
}
